<template>
  <div class="sorting">
    <div
      class="sorting__title"
      @click="togglePopoverHandler"
    >
      <SortingIcon />
      <span>{{ title }}</span>
    </div>
    <UiModal
      v-if="isMobile"
      :is-shown="isOpenPopover"
      modal-class="sorting-modal"
      is-footer-hidden
      is-scrollbar-width-ignored
      :title="t('sorting.title')"
      @close="closePopover"
    >
      <template #content>
        <div class="sorting-modal-content">
          <div
            v-for="(item, i) in listSort"
            :key="i"
            class="sorting-element"
          >
            <UiRadio
              :id="item.id"
              data-test="mobile-sort-radio"
              name="sort-radio"
              :label="item.label"
              :isChecked="isChosenSortValue(item.value)"
              @input-value="chooseSortingValue(item.value)"
            />
          </div>
        </div>
      </template>
    </UiModal>

    <ui-popover
      v-else
      :open="isOpenPopover"
      @close="closePopover"
    >
      <template #content>
        <div class="sorting__list">
          <div
            v-for="item of listSort"
            :key="item.value"
            class="sorting-desktop__list-item"
            @click="chooseSortingValue(item.value)"
          >
            <span>
              {{ item.label }}
            </span>
            <CheckMarkIcon v-if="isChosenSortValue(item.value)" />
          </div>
        </div>
      </template>
    </ui-popover>
  </div>
</template>

<script setup lang="ts">
  import { UiModal } from '@experience/core-modal-ui';
  import { useScreenSize } from '@experience/shared';
  import { UiRadio } from '@experience/core-radio-ui';
  import { UiPopover } from '@experience/vue3-kit';

  import { SortingIcon } from '@/shared/ui/icons/sortingIcon';
  import { useI18n } from '@experience/i18n';
  import ru from '../../i18n/ru.json';
  import { ListSortItemValue } from '@/features/filter/sorting/const/config';
  import { CheckMarkIcon } from '@/shared/ui/icons/checkMarkIcon';
  import { useIsRussianTourLocation } from '@/entities/city';
  import { useTourFiltersStore } from '@/entities/filter/store/tour';
  import { sortingScroll } from '@/shared/lib/use-scroll';
  import type { ContextGeoSubject } from '@/shared/types/geography.ts';
  import { computed, ref, type useShadowRoot } from 'vue';
  import { useTourSortingClickTracker } from '@/features/filter/sorting/composables/tour/useTourSortingClickTracker.ts';
  import { useHeaderZIndex } from '@/shared/composables/useHeaderZIndex.ts';

  interface Props {
    defaultSoguFields: ContextGeoSubject;
  }

  const props = defineProps<Props>();

  const { t } = useI18n({
    inheritLocale: true,
    messages: { ru },
  });

  const { isMobile } = useScreenSize();
  const filtersStore = useTourFiltersStore();
  const { trackSortingClick } = useTourSortingClickTracker();
  const { decreaseZIndex, increaseZIndex } = useHeaderZIndex();
  const { isRussianLocation } = useIsRussianTourLocation();

  const isOpenPopover = ref(false);

  const rootNode = inject<ReturnType<typeof useShadowRoot>>('shadowRoot', null);

  const title = computed(() => {
    switch (filtersStore.searchState.sorting) {
      case ListSortItemValue.PUBLISHED_AT:
        return t('sorting.publishedAt');
      case ListSortItemValue.PRICE:
        return t('sorting.price');
      case ListSortItemValue.RATING:
        return t('sorting.rating');
      case ListSortItemValue.DISCOUNT:
        return t('sorting.discount');
      default:
        return t('sorting.rank');
    }
  });

  const listSort = computed(() => {
    const config = {
      rank: {
        label: t('sorting.rank'),
        value: ListSortItemValue.RANK,
      },
      price: {
        label: t('sorting.price'),
        value: ListSortItemValue.PRICE,
      },
      publishedAt: {
        label: t('sorting.publishedAt'),
        value: ListSortItemValue.PUBLISHED_AT,
      },
      rating: {
        label: t('sorting.rating'),
        value: ListSortItemValue.RATING,
      },
      discount: {
        label: t('sorting.discount'),
        value: ListSortItemValue.DISCOUNT,
      },
    } as const;

    type Key = keyof typeof config;
    let order: Key[];

    if (isRussianLocation.value) {
      order = ['rank', 'price', 'publishedAt', 'rating', 'discount'];
    } else {
      order = ['rank', 'publishedAt', 'rating', 'discount'];
    }

    return order.map((key, idx) => ({
      id: idx + 1,
      ...config[key],
    }));
  });

  const togglePopoverHandler = () => {
    if (isMobile.value) {
      decreaseZIndex();
    }

    isOpenPopover.value = !isOpenPopover.value;
  };

  const closePopover = () => {
    if (isMobile.value) {
      increaseZIndex();
    }
    isOpenPopover.value = false;
  };

  const chooseSortingValue = (item: string) => {
    filtersStore.state.sorting = item;
    filtersStore.search();

    closePopover();
    trackSortingClick(props.defaultSoguFields);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        sortingScroll(rootNode, isMobile.value);
      });
    });
  };

  const isChosenSortValue = (item: string) => {
    return item === filtersStore.state.sorting;
  };
</script>

<style scoped lang="scss">
  @use '@experience/design-tokens/typography' as typography;
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  .sorting {
    position: relative;
    z-index: 9;

    :deep(.popover) {
      right: 0;
      left: auto;
      overflow: hidden;
      padding: 0;
      border-radius: 8px;
    }
  }

  .sorting-modal {
    margin-top: -16px;
  }

  .sorting__title {
    display: flex;
    align-items: center;
    cursor: pointer;
    column-gap: 4px;

    @include typography.base-400-400-medium;

    @include adaptivity.tablet {
      @include typography.base-300-300-medium;
    }
  }

  .sorting__list {
    width: 200px;
    padding: 4px 0;
  }

  .sorting-desktop__list-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    white-space: nowrap;
    cursor: pointer;

    @include typography.base-400-400-regular;

    &:hover {
      background: var(--comp-dropdown-menu-item-color-background-hovered);
    }
  }

  .sorting-element {
    :deep(.radio) {
      padding: 12px 0;
    }
  }
</style>
<style lang="scss">
  .ui-modal.sorting-modal {
    .ui-modal__body-content-wrapper {
      padding-top: 0;
    }

    .ui-modal-header__content {
      align-items: center;
    }
  }
</style>
